import React, { useEffect, useRef, useState } from "react";
import { Button, ConfigProvider, Dropdown, Input, Modal, Typography, message, theme as antdTheme } from "antd";
import type { InputRef } from "antd";
import {
  GoogleOutlined,
  LoginOutlined,
  LogoutOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useIntl } from "react-intl";
import { API } from "../apiUrl";
import type { ThemeName } from "./BottomToolbar";

const { Text } = Typography;

const USER_KEY = "inoneyear-user";
const TOKEN_KEY = "inoneyear-token";
const META_KEY = "inoneyear-user-meta";
export const AUTH_CHANGED_EVENT = "inoneyear:auth-changed";

interface UserMeta {
  email: string;
  name?: string | null;
}

const getUserMeta = (): UserMeta | null => {
  try {
    const raw = localStorage.getItem(META_KEY);
    return raw ? (JSON.parse(raw) as UserMeta) : null;
  } catch {
    return null;
  }
};

const saveUserMeta = (email: string, name?: string | null): void => {
  try {
    if (name) {
      localStorage.setItem(META_KEY, JSON.stringify({ email, name }));
    } else {
      localStorage.removeItem(META_KEY);
    }
  } catch {}
};

export const getLocalUser = (): string | null => {
  try {
    return localStorage.getItem(USER_KEY);
  } catch {
    return null;
  }
};

export const getToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

const truncate = (value: string): string => (value.length > 18 ? `${value.slice(0, 14)}…` : value);
const validEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

// 跨站登录同步：登录成功后把 JWT 静默推送到 LuminaVerse（lv.aliving.stream）
const SYNC_PEER_URL = "https://lv.aliving.stream";

const syncLogin = (token: string): void => {
  try {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.referrerPolicy = "no-referrer";
    iframe.src = `${SYNC_PEER_URL}/?token=${encodeURIComponent(token)}&sync_from=inoneyear`;
    const remove = () => iframe.remove();
    iframe.onload = () => window.setTimeout(remove, 3000);
    window.setTimeout(remove, 15000);
    document.body.appendChild(iframe);
  } catch {}
};

const syncLogout = (): void => {
  try {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.referrerPolicy = "no-referrer";
    iframe.src = `${SYNC_PEER_URL}/?logout=1&sync_from=inoneyear`;
    const remove = () => iframe.remove();
    iframe.onload = () => window.setTimeout(remove, 3000);
    window.setTimeout(remove, 15000);
    document.body.appendChild(iframe);
  } catch {}
};

// 主题 → antd token，让弹窗里的 Input/Button 跟随 light / classic / dark
const antdThemeConfig = (theme: ThemeName) => {
  if (theme === "dark") {
    return {
      algorithm: antdTheme.darkAlgorithm,
      token: {
        colorPrimary: "#ff6b6b",
        colorBgContainer: "#1f1f1f",
        colorText: "#eee",
        colorTextSecondary: "#999",
        colorBorder: "#2a2a2a",
        borderRadius: 8,
      },
    };
  }
  if (theme === "classic") {
    return {
      token: {
        colorPrimary: "#8b6f47",
        colorBgContainer: "#fff",
        colorText: "#2b2b2b",
        colorTextSecondary: "#888",
        colorBorder: "#eee7db",
        borderRadius: 8,
      },
    };
  }
  return {
    token: {
      colorPrimary: "#e63946",
      colorBgContainer: "#fff",
      colorText: "#333",
      colorTextSecondary: "#999",
      colorBorder: "#f0f0f0",
      borderRadius: 8,
    },
  };
};

interface AuthPanelProps {
  theme?: ThemeName;
}

const AuthPanel: React.FC<AuthPanelProps> = ({ theme = "classic" }) => {
  const intl = useIntl();
  const [user, setUser] = useState<string | null>(getLocalUser);
  const [userMeta, setUserMeta] = useState<UserMeta | null>(getUserMeta);
  const [modalOpen, setModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const emailRef = useRef<InputRef>(null);
  const codeRef = useRef<InputRef>(null);

  // Google OAuth 回调：worker 302 回 APP_URL?token=xxx / ?error=xxx
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    const token = params.get("token");
    const logout = params.get("logout");

    if (logout) {
      try {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(META_KEY);
      } catch {}
      setUser(null);
      setUserMeta(null);
      window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
      window.history.replaceState({}, "", window.location.pathname + window.location.hash);
      return;
    }

    if (!error && !token) return;

    if (error) {
      message.error(error);
    } else if (token) {
      try {
        localStorage.setItem(TOKEN_KEY, token);
      } catch {}
      let account: string | null = null;
      try {
        const payload = JSON.parse(
          atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
        ) as { email?: string };
        account = payload.email || null;
      } catch {}
      if (account) {
        try {
          localStorage.setItem(USER_KEY, account);
        } catch {}
        setUser(account);
      }
      message.success(intl.formatMessage({ id: "auth.loginSuccess" }));
      window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
      if (!params.get("sync_from")) syncLogin(token);
    }
    // 清掉 URL 上的 token/error，避免刷新后重复处理
    window.history.replaceState({}, "", window.location.pathname + window.location.hash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore the session by validating the stored JWT against the worker.
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!res.ok) {
          try {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
            localStorage.removeItem(META_KEY);
          } catch {}
          setUser(null);
          setUserMeta(null);
          return;
        }
        const data = (await res.json()) as { user?: { email?: string; name?: string | null } };
        const restored = data.user?.email || getLocalUser();
        if (!restored) return;
        try {
          localStorage.setItem(USER_KEY, restored);
        } catch {}
        saveUserMeta(restored, data.user?.name);
        setUser(restored);
        setUserMeta(getUserMeta());
        window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
      } catch {
        // Worker unreachable — keep the local session as-is.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeModal = () => {
    setModalOpen(false);
    setStep("email");
    setEmail("");
    setCode("");
  };

  const handleSendCode = async () => {
    const em = email.trim().toLowerCase();
    if (!validEmail(em)) {
      message.error(intl.formatMessage({ id: "auth.validEmail" }));
      emailRef.current?.focus();
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/auth/send-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: em }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (res.ok) {
        setStep("code");
        setCode("");
        message.success(intl.formatMessage({ id: "auth.codeSent" }));
        window.setTimeout(() => codeRef.current?.focus(), 50);
      } else if (res.status === 429) {
        message.error(intl.formatMessage({ id: "auth.tooManyRequests" }));
      } else {
        message.error(data.error || intl.formatMessage({ id: "auth.sendCodeFailed" }));
      }
    } catch {
      message.error(intl.formatMessage({ id: "auth.networkError" }));
    } finally {
      setSending(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/google-config`);
      const data = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
      if (!res.ok || !data.url) {
        message.error(data.error || intl.formatMessage({ id: "auth.networkError" }));
        return;
      }
      // 整页跳转到 Google 授权页，完成后由 worker 302 回本页
      window.location.href = data.url;
    } catch {
      message.error(intl.formatMessage({ id: "auth.networkError" }));
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleVerify = async (raw?: string) => {
    const c = (raw ?? code).trim();
    if (!c) {
      message.error(intl.formatMessage({ id: "auth.codeRequired" }));
      codeRef.current?.focus();
      return;
    }
    setVerifying(true);
    try {
      const res = await fetch(`${API}/api/auth/verify-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), code: c }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        token?: string;
        user?: { email?: string; name?: string | null };
        error?: string;
      };
      if (res.ok && data.token) {
        const account = data.user?.email || email.trim().toLowerCase();
        try {
          localStorage.setItem(TOKEN_KEY, data.token);
          localStorage.setItem(USER_KEY, account);
        } catch {}
        saveUserMeta(account, data.user?.name);
        setUser(account);
        setUserMeta(getUserMeta());
        closeModal();
        window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
        message.success(intl.formatMessage({ id: "auth.loginSuccess" }));
        syncLogin(data.token);
      } else if (res.status === 401) {
        message.error(intl.formatMessage({ id: "auth.invalidCode" }));
        setCode("");
        codeRef.current?.focus();
      } else {
        message.error(data.error || intl.formatMessage({ id: "auth.verifyFailed" }));
      }
    } catch {
      message.error(intl.formatMessage({ id: "auth.networkError" }));
    } finally {
      setVerifying(false);
    }
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(META_KEY);
    } catch {}
    setUser(null);
    setUserMeta(null);
    window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
    syncLogout();
  };

  const loginModal = (
    <ConfigProvider theme={antdThemeConfig(theme)}>
      <Modal
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        centered
        width={400}
        className="auth-modal"
        destroyOnClose
      >
        <div className="auth-modal-head">
          <div className="auth-modal-title">
            <SafetyCertificateOutlined className="auth-modal-icon" />
            <span>{intl.formatMessage({ id: "auth.modalTitle" })}</span>
          </div>
          <div className="auth-modal-sub">{intl.formatMessage({ id: "auth.modalSubtitle" })}</div>
        </div>

        {step === "email" ? (
          <div className="auth-modal-step">
            <Button
              size="large"
              block
              icon={<GoogleOutlined />}
              loading={googleLoading}
              onClick={handleGoogleLogin}
              className="auth-google-btn"
            >
              {intl.formatMessage({ id: "auth.continueWithGoogle" })}
            </Button>
            <div className="auth-divider">
              <span>{intl.formatMessage({ id: "settings.or" })}</span>
            </div>
            <Input
              ref={emailRef}
              size="large"
              prefix={<MailOutlined />}
              placeholder={intl.formatMessage({ id: "auth.emailPlaceholder" })}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onPressEnter={() => handleSendCode()}
              autoFocus
              allowClear
            />
            <Button type="primary" size="large" block loading={sending} onClick={() => handleSendCode()}>
              {intl.formatMessage({ id: "auth.sendCode" })}
            </Button>
            <div className="auth-modal-note">{intl.formatMessage({ id: "auth.benefits" })}</div>
          </div>
        ) : (
          <div className="auth-modal-step">
            <div className="auth-sent-hint">
              <span>{intl.formatMessage({ id: "auth.codeSentTo" }, { email })}</span>
              <button
                type="button"
                className="auth-link"
                onClick={() => {
                  setStep("email");
                  setCode("");
                }}
              >
                {intl.formatMessage({ id: "auth.changeEmail" })}
              </button>
            </div>
            <Input
              ref={codeRef}
              size="large"
              prefix={<SafetyCertificateOutlined />}
              placeholder={intl.formatMessage({ id: "auth.code" })}
              value={code}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
                setCode(digits);
                if (digits.length === 6) handleVerify(digits);
              }}
              onPressEnter={() => handleVerify()}
              autoFocus
              inputMode="numeric"
              className="auth-code-input"
            />
            <Button type="primary" size="large" block loading={verifying} onClick={() => handleVerify()}>
              {intl.formatMessage({ id: "auth.verifyAndLogin" })}
            </Button>
          </div>
        )}
      </Modal>
    </ConfigProvider>
  );

  if (!user) {
    return (
      <>
        <Button size="small" icon={<LoginOutlined />} onClick={() => setModalOpen(true)}>
          {intl.formatMessage({ id: "auth.login" })}
        </Button>
        {loginModal}
      </>
    );
  }

  return (
    <>
      <Dropdown
        trigger={["click"]}
        placement="bottomRight"
        menu={{
          items: [
            {
              key: "account",
              label: (
                <Text style={{ color: "var(--muted)" }}>
                  {userMeta?.name ? `${userMeta.name} · ${user}` : user}
                </Text>
              ),
              disabled: true,
            },
            { type: "divider" },
            {
              key: "logout",
              label: intl.formatMessage({ id: "auth.logout" }),
              icon: <LogoutOutlined />,
              onClick: handleLogout,
            },
          ],
        }}
      >
        <Button size="small" icon={<UserOutlined />}>
          {truncate(userMeta?.name || user)}
        </Button>
      </Dropdown>
      {loginModal}
    </>
  );
};

export default AuthPanel;
